<script lang="ts">
  import { onMount, onDestroy } from 'svelte'

  interface Props {
    videoId: string
    startTime?: number
    onTimeUpdate?: (time: number) => void
    onDurationChange?: (duration: number) => void
    onTitleChange?: (title: string) => void
    onPlay?: () => void
  }

  let { videoId, startTime = 0, onTimeUpdate, onDurationChange, onTitleChange, onPlay }: Props = $props()

  let playerElement: HTMLDivElement
  let player: YT.Player | null = null
  let timeUpdateInterval: number | null = null

  function extractVideoId(input: string): string {
    const urlMatch = input.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&]+)/)
    return urlMatch ? urlMatch[1] : input
  }

  function initPlayer() {
    const id = extractVideoId(videoId)

    if (typeof YT !== 'undefined' && YT.Player) {
      player = new YT.Player(playerElement, {
        videoId: id,
        playerVars: {
          autoplay: 0,
          modestbranding: 1,
          rel: 0,
          start: startTime,
        },
        events: {
          onReady: handleReady,
          onStateChange: handleStateChange,
        },
      })
    }
  }

  function handleReady() {
    if (onDurationChange && player) {
      onDurationChange(player.getDuration())
    }
    if (onTitleChange && player) {
      const videoData = player.getVideoData()
      if (videoData?.title) {
        onTitleChange(videoData.title)
      }
    }
  }

  function handleStateChange(event: YT.OnStateChangeEvent) {
    if (event.data === YT.PlayerState.PLAYING) {
      startTimeTracking()
      if (onDurationChange && player) {
        onDurationChange(player.getDuration())
      }
      onPlay?.()
    } else {
      stopTimeTracking()
    }
  }

  function startTimeTracking() {
    if (timeUpdateInterval) return

    timeUpdateInterval = window.setInterval(() => {
      if (player && onTimeUpdate) {
        const currentTime = player.getCurrentTime()
        onTimeUpdate(currentTime)
      }
    }, 1000)
  }

  function stopTimeTracking() {
    if (timeUpdateInterval) {
      clearInterval(timeUpdateInterval)
      timeUpdateInterval = null
    }
  }

  export function seekTo(seconds: number, offset: number = 0) {
    player?.seekTo(Math.max(0, seconds - offset), true)
  }

  export function getCurrentTime(): number {
    return player?.getCurrentTime() ?? 0
  }

  export function togglePlay() {
    if (!player) return
    const state = player.getPlayerState()
    if (state === YT.PlayerState.PLAYING) {
      player.pauseVideo()
    } else {
      player.playVideo()
    }
  }

  onMount(() => {
    if (typeof YT !== 'undefined') {
      initPlayer()
    } else {
      const tag = document.createElement('script')
      tag.src = 'https://www.youtube.com/iframe_api'
      document.head.appendChild(tag)

      ;(window as any).onYouTubeIframeAPIReady = initPlayer
    }
  })

  onDestroy(() => {
    stopTimeTracking()
    player?.destroy()
  })
</script>

<div class="youtube-player" bind:this={playerElement}></div>

<style>
  .youtube-player {
    width: 100%;
    height: 100%;
  }

  .youtube-player :global(iframe) {
    width: 100%;
    height: 100%;
  }
</style>
